import { Languages } from "@tree-company/collect-js-client";
import translate from "./locales";
import { THEMES } from "./themes";
import { Redirect } from "./components/redirect";
import { TellitPage } from "./components/tellit_page";
import { PageComponent } from "./components/page_component";
import { client } from "./app";
import { STREETS } from "./neighbourhoods";
import NeighbourhoodFinder from "./components/neighbourhood_finder";
import { RadioLayout } from "@tree-company/tree-query";

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}

function translatedPropertyFromKey(translation_key, interpolations) {
  return {
    nl: translate(translation_key, interpolations, Languages.nl),
  };
}

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  const treesponses_length = client.treesponses.length;
  if (treesponses_length >= 2) {
    return client.treesponses[treesponses_length - 2]?.getAnswer(key);
  }
}

const aiResponseTexts = {
  proficiencyOkToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_ok",
  ),
  proficiencyOkToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_not_ok",
  ),
  proficiencyHighToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_ok",
  ),
  proficiencyHighToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_ok",
  ),
  proficiencyLowToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_not_ok",
  ),
};

const namePage = {
  key: "name",
  order: 4,
  component: PageComponent,
  skipIf: (treesponse) => {
    const comment = treesponse.getAnswer("comment");
    return typeof comment !== "string" || comment.length === 0;
  },
  description: translatedPropertyFromKey(["name", "intro", "description"]),
  sections: [
    {
      order: 1,
      type: "string",
      key: "firstName",
      title: translatedPropertyFromKey(["name", "firstName", "title"]),
      required: true,
      defaultValue: () => getPreviousAnswer("firstName"),
    },
    {
      order: 2,
      type: "string",
      key: "lastName",
      title: translatedPropertyFromKey(["name", "lastName", "title"]),
      required: true,
      defaultValue: () => getPreviousAnswer("lastName"),
    },
    {
      order: 3,
      type: "hidden",
      key: "completed",
      defaultValue: true,
    },
  ],
};

const neighbourhoodPage = {
  key: "neighbourhood",
  order: 5,
  component: PageComponent,
  title: translatedPropertyFromKey(["wijk", "intro", "title"]),
  description: translatedPropertyFromKey(["wijk", "intro", "description"]),
  // We skip this page if the user answered the same name as their previous answer
  skipIf: (treesponse) => {
    return ["firstName", "lastName"].every((key) => {
      const prev = treesponse.getAnswer(key);
      return (
        typeof prev === "string" &&
        prev.length > 0 &&
        prev === getPreviousAnswer(key)
      );
    });
  },
  sections: [
    {
      order: 1,
      type: "radio",
      component: NeighbourhoodFinder,
      key: "wijk",
      title: translatedPropertyFromKey(["wijk", "wijk", "title"]),
      options: [
        "savelkoul",
        "sint-jozef",
        "mortsel-dorp",
        "fort-4",
        "hof-van-rieth",
        "oude-god",
        "geveart",
        "dieseghem",
        "geen",
      ].map((value) => ({
        label: translatedPropertyFromKey(["wijk", "wijk", "options", value]),
        value,
      })),
      required: true,
      data: STREETS,
      defaultValue: () => getPreviousAnswer("wijk"),
    },
  ],
};

const sociodemoPage = {
  key: "sociodemo",
  order: 6,
  component: PageComponent,
  title: translatedPropertyFromKey(["sociodemo", "intro", "title"]),
  description: translatedPropertyFromKey(["sociodemo", "intro", "description"]),
  // We skip this page if the user answered the same name as their previous answer
  skipIf: (treesponse) => {
    return ["firstName", "lastName"].every(
      (key) => treesponse.getAnswer(key) === getPreviousAnswer(key),
    );
  },
  sections: [
    {
      order: 1,
      type: "number",
      key: "yearOfBirth",
      title: translatedPropertyFromKey(["sociodemo", "year_of_birth", "title"]),
      options: [],
      defaultValue: () => getPreviousAnswer("year_of_birth"),
      maxLength: 4,
      placeholder: translatedPropertyFromKey([
        "sociodemo",
        "year_of_birth",
        "placeholder",
      ]),
    },
  ],
};

const likePage = {
  key: "like",
  order: 7,
  component: PageComponent,
  sections: [
    {
      type: "like",
      order: 1,
      key: "like",
      title: translatedPropertyFromKey(["like", "title"]),
      description: translatedPropertyFromKey(["like", "description"]),
      aggregatorSettings: {
        url: `${aggregatorURL}/mjp/mortsel`,
        projectKey: import.meta.env.VITE_AGGREGATOR_KEY,
      },
    },
  ],
};

const optinPage = {
  key: "optin",
  order: 8,
  component: PageComponent,
  title: translatedPropertyFromKey(["optin", "intro", "title"]),
  // We only show the opt in page if the user has not encountered it before
  skipIf: () => client.treesponses.some((t) => t.page?.name === "completed"),
  sections: [
    {
      type: "checkbox",
      order: 1,
      title: translatedPropertyFromKey(["optin", "bpact", "title"]),
      description: translatedPropertyFromKey(["optin", "bpact", "description"]),
      key: "bpact",
    },
    {
      type: "email",
      order: 2,
      skipIf: (treesponse) => treesponse.getAnswer("bpact") !== true,
      dependsOn: ["bpact"],
      title: translatedPropertyFromKey(["optin", "email", "title"]),
      key: "email",
      customValidations: [
        (treesponse, _key, value) => {
          if (
            treesponse.getAnswer("bpact") &&
            (value === null || value === undefined)
          ) {
            return [
              false,
              translatedPropertyFromKey(["optin", "email", "required"]),
            ];
          }

          return [true, null];
        },
      ],
    },
  ],
};

const redirectPage = {
  key: "completed",
  order: 9,
  component: Redirect,
  sections: [],
};

export function surveySettingsForTheme(theme) {
  return {
    key: `mjp-mortsel-${theme}`,
    pages: [
      {
        key: "tellit",
        order: 1,
        component: TellitPage,
        sections: [
          {
            type: "radio",
            htmlAttributes: { class: "question--tellit" },
            order: 1,
            key: "actions",
            layout: RadioLayout.buttons,
            title: translatedPropertyFromKey([theme, "actions", "title"]),
            description: translatedPropertyFromKey([
              theme,
              "actions",
              "description",
            ]),
            required: true,
            options: THEMES[theme].map((action) => ({
              label: translatedPropertyFromKey([
                theme,
                "actions",
                "options",
                action,
              ]),
              value: action,
            })),
          },
        ],
      },
      {
        key: `comment-${theme}`,
        order: 3,
        component: PageComponent,
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            maxLength: 400,
            title: translatedPropertyFromKey([
              theme,
              "comment_question",
              "title",
            ]),
            placeholder: translatedPropertyFromKey([
              theme,
              "comment_question",
              "placeholder",
            ]),
            aiResponseTexts,
          },
        ],
      },
      namePage,
      neighbourhoodPage,
      sociodemoPage,
      likePage,
      optinPage,
      redirectPage,
    ],
  };
}
